import { type RefObject, useEffect, useRef, useState } from "react";

/** Whether an element has come within `rootMargin` of the viewport, once:
 *  false until it does, true from then on. Heavy blocks in a long list
 *  (code viewers, charts) wait on this before building or fetching, so a
 *  conversation costs what is on screen, not what it holds. Without an
 *  IntersectionObserver (tests, old engines) everything counts as near. */
export function useNearViewport<T extends HTMLElement>(
  rootMargin = "400px 0px",
): [RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (near || !el) return;
    if (typeof IntersectionObserver === "undefined") {
      setNear(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setNear(true);
      },
      { rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [near, rootMargin]);
  return [ref, near];
}
